import React from "react";
import styled, { css } from "styled-components";

import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import { ButtonLink } from "src/library/components/Link";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils";

interface DefaultBranchProps{
  branchId?: number
  terminology: ITerminology
}

/**
 * This is a component which presents the default branch with a "view" button.
 * it takes in a branch id so could technically show any branch.
 */
const DefaultBranch: React.ReactFCWithChildren<DefaultBranchProps> = props => {
  const { branchId, terminology } = props;
  const [ defaultBranchRes, getDefaultBranch ] = useDropsApi("GET_DROP");
  const [ defaultBranch, setDefaultBranch ] = React.useState<IDrop | null>(null);

  React.useEffect(() => {
    if (branchId) {
      getDefaultBranch(branchId);
    }
  }, [ branchId, getDefaultBranch ]);

  React.useEffect(() => {
    if (defaultBranchRes.data && defaultBranchRes.complete) {
      setDefaultBranch(defaultBranchRes.data);
    }
  }, [ defaultBranchRes ]);

  return (
    <DefaultBranchWrapper>
      {defaultBranch ? (
        <div className="default-branch">
          <div className="title-branch">
            <div className="title">
              <Text
                as="h4"
              >
                {`Default ${terminology.drop.singular.text}`}
                :
              </Text>
            </div>

            <div className="branch-spinner">
              <div className="branch">
                <Text as="h3">
                  {`${defaultBranch.branchNumber} - ${defaultBranch.name} `}
                </Text>

                <ButtonLink
                  $buttonType="primary"
                  className="orders-button"
                  to={`/admin/customers/${defaultBranch.customerId}/drops/${defaultBranch.id}/orders`}
                >
                  View
                </ButtonLink>
              </div>

              {defaultBranchRes.pending && defaultBranch ? (
                <div className="spinner-container">
                  <LoadingSpinner
                    type="flat"
                    position="inline"
                  />
                </div>
              ) : <></>}
            </div>
          </div>
        </div>
      ) : (
        <div className="spinner-container">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      )}
    </DefaultBranchWrapper>
  );
};

const DefaultBranchWrapper = styled.div`
  ${({ theme }) => css`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${theme.spacing.margins.md};

  .default-branch{
    display: flex;
    flex-direction: column
  }
  
  .title-branch{
    display: flex;
    flex-direction: column
  }

  .branch-spinner{
    display: flex;
    flex-direction: row;
    height:min-content;
  }

  .branch{
    border-radius: 5px;
    background-color:${theme.colors.lightestGrey};
    padding: ${theme.spacing.padding.md};
    display: flex;
    flex-direction: row;
    align-items:center;
  }

  .title{
    margin-top: ${theme.spacing.margins.sm};
    justify-self: center;
    margin-bottom: ${theme.spacing.margins.sm};
  }
  
  .default-branch h3 {
    margin-bottom: 0px;
    padding-right: ${theme.spacing.padding.md};
  }
  
  .default-branch h4{
    margin-bottom:0px;
  }
  
  .spinner-container {
    display: flex;
    margin: ${theme.spacing.padding.sm};
    align-items: center;
`}`;

export default DefaultBranch;
